import { Fragment, type ReactNode } from 'react';

import { safeHttpsUrl } from '@/_shared/lib/safe-url';

/**
 * Minimal Slack mrkdwn → React renderer for the message preview. Covers the inline subset our
 * templates emit — `*bold*`, `_italic_`, `~strike~`, `` `code` ``, `<url|label>` links — plus literal
 * newlines. Delimiters honour Slack's whitespace-flanking rule so snake_case (row_count) is left alone.
 * NOT a full parser: no code fences, blockquotes, `<@mentions>`, `:emoji:` shortcodes, or nesting.
 */

// One line, one pass: code | *bold* | _italic_ | ~strike~ | <url|label>. Emphasis delimiters must be
// flanked by whitespace/boundaries and wrap a non-space run, matching how Slack decides they apply.
const INLINE = [
  '`([^`]+)`',
  '(?<=\\s|^)\\*([^*\\s][^*]*?)\\*(?=[\\s.,!?:;)]|$)',
  '(?<=\\s|^)_([^_\\s][^_]*?)_(?=[\\s.,!?:;)]|$)',
  '(?<=\\s|^)~([^~\\s][^~]*?)~(?=[\\s.,!?:;)]|$)',
  '<([^>|\\s]+)(?:\\|([^>]+))?>',
].join('|');

function renderLine(line: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = new RegExp(INLINE, 'g');
  let last = 0;
  let n = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) out.push(line.slice(last, m.index));
    const key = `${keyBase}.${n++}`;
    if (m[1] != null) {
      out.push(
        <code key={key} className="rounded bg-muted px-1 font-mono text-[0.75rem]">
          {m[1]}
        </code>,
      );
    } else if (m[2] != null) {
      out.push(
        <strong key={key} className="font-semibold">
          {m[2]}
        </strong>,
      );
    } else if (m[3] != null) {
      out.push(<em key={key}>{m[3]}</em>);
    } else if (m[4] != null) {
      out.push(<del key={key}>{m[4]}</del>);
    } else {
      // Slack link `<url|label>` (label optional) — render only https targets; anything else is text.
      const href = safeHttpsUrl(m[5]);
      const label = m[6] ?? m[5];
      out.push(
        href ? (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2"
          >
            {label}
          </a>
        ) : (
          label
        ),
      );
    }
    last = re.lastIndex;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

/** Render Slack mrkdwn as React nodes, with each `\n` becoming a hard line break. */
export function renderSlackMrkdwn(message: string): ReactNode {
  return message.split('\n').map((line, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {renderLine(line, String(i))}
    </Fragment>
  ));
}
